"use strict";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  isRedoJobActive,
  listRedoJobs,
  startRedoJob,
  stopRedoJob,
} from "../../features/projectWork/voiceRedoJobs.js";

const POLL_INTERVAL_MS = 2000;

/**
 * Server-side runs that redo a character's lines with a new voice, for one
 * project and language, keyed by speaker. Loads them on open, polls while one
 * is running and calls `onJobFinished` when a run ends.
 *
 * Only runs seen running in this session are "visible": finished runs from
 * earlier sessions stay out of the way until a character is redone again.
 */
const useVoiceRedoJobs = ({ projectId, languageCode, onJobFinished }) => {
  const [jobs, setJobs] = useState({});
  const [visibleSpeakers, setVisibleSpeakers] = useState(() => new Set());
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const onJobFinishedRef = useRef(onJobFinished);
  onJobFinishedRef.current = onJobFinished;

  const applyJobs = useCallback((list, { initial = false } = {}) => {
    const next = Object.fromEntries(list.map((job) => [job.speakerId, job]));
    const previous = jobsRef.current;

    Object.values(next).forEach((job) => {
      if (
        !initial &&
        isRedoJobActive(previous[job.speakerId]) &&
        !isRedoJobActive(job)
      ) {
        onJobFinishedRef.current?.(job);
      }
    });

    setVisibleSpeakers((visible) => {
      const updated = new Set(visible);
      Object.values(next).forEach((job) => {
        if (isRedoJobActive(job)) updated.add(job.speakerId);
      });
      return updated.size === visible.size ? visible : updated;
    });
    setJobs(next);
  }, []);

  const refresh = useCallback(
    async (options) => {
      if (!projectId || !languageCode) return;
      try {
        applyJobs(await listRedoJobs({ projectId, languageCode }), options);
      } catch (error) {
        console.error("Unable to load voice redo jobs:", error);
      }
    },
    [applyJobs, languageCode, projectId]
  );

  useEffect(() => {
    setJobs({});
    setVisibleSpeakers(new Set());
    refresh({ initial: true });
  }, [refresh]);

  const hasActiveJob = Object.values(jobs).some(isRedoJobActive);
  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const timer = setInterval(() => refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActiveJob, refresh]);

  const start = useCallback(
    async ({ speakerId, voiceId, dialogueIds }) => {
      const job = await startRedoJob({
        projectId,
        languageCode,
        speakerId,
        voiceId,
        dialogueIds,
      });
      setJobs((previous) => ({ ...previous, [speakerId]: job }));
      setVisibleSpeakers((visible) => new Set(visible).add(speakerId));
      return job;
    },
    [languageCode, projectId]
  );

  const stop = useCallback(
    async (speakerId) => {
      const job = jobsRef.current[speakerId];
      if (!job) return;
      const updated = await stopRedoJob({ projectId, jobId: job.id });
      setJobs((previous) => ({ ...previous, [speakerId]: updated }));
    },
    [projectId]
  );

  const dismiss = useCallback((speakerId) => {
    setVisibleSpeakers((visible) => {
      const updated = new Set(visible);
      updated.delete(speakerId);
      return updated;
    });
  }, []);

  const visibleJobs = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(jobs).filter(([speakerId]) =>
          visibleSpeakers.has(speakerId)
        )
      ),
    [jobs, visibleSpeakers]
  );

  const runningSpeakers = useMemo(
    () =>
      Object.keys(jobs).filter((speakerId) => isRedoJobActive(jobs[speakerId])),
    [jobs]
  );

  return { visibleJobs, runningSpeakers, start, stop, dismiss };
};

export default useVoiceRedoJobs;
